import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useLang from '../hooks/useLang'
import { supabase } from '../supabaseClient'
import {
  TrendingUp, Users, BarChart3, Gift, ArrowUpRight, CheckCircle2,
  ClipboardList, Plug, Sparkles, Lock
} from 'lucide-react'
import './Home.css'

import logoQaraa from '../images/logo-qaraa.webp'
import qaraaCrmLogo from '../images/qaraaxqaraa-crm.webp'

const glassCard = {
  background: 'rgba(255, 255, 255, 0.82)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: '28px',
  boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
  border: '1px solid rgba(255,255,255,0.6)',
}

function GlassBenefit({ icon, title, text }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...glassCard,
        padding: '30px',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered ? '0 16px 48px rgba(0,0,0,0.10)' : '0 8px 40px rgba(0,0,0,0.06)',
      }}
    >
      <div style={{
        width: '44px', height: '44px', borderRadius: '14px',
        background: 'rgba(0, 122, 255, 0.1)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', marginBottom: '18px'
      }}>
        {icon}
      </div>
      <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#1C1C1E', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{title}</h3>
      <p style={{ fontSize: '14.5px', color: '#636366', lineHeight: '1.55', margin: 0 }}>{text}</p>
    </div>
  )
}

function StepItem({ number, title, text }) {
  return (
    <div style={{ display: 'flex', gap: '18px', flex: 1 }}>
      <div style={{
        flexShrink: 0, width: '40px', height: '40px', borderRadius: '50%',
        background: '#1C1C1E', color: '#FFF', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '600'
      }}>
        {number}
      </div>
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1C1C1E', margin: '6px 0 6px', letterSpacing: '-0.01em' }}>{title}</h3>
        <p style={{ fontSize: '14.5px', color: '#636366', lineHeight: '1.55', margin: 0, maxWidth: '260px' }}>{text}</p>
      </div>
    </div>
  )
}

export default function Business() {
  const { lang, changeLang, t, languages } = useLang()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [langDropdownOpen, setLangDropdownOpen] = useState(false)
  const langRef = useRef(null)
  const formRef = useRef(null)

  const [form, setForm] = useState({ business_name: '', contact_name: '', phone: '', city: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => { window.scrollTo(0, 0) }, [])

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  const handleSubmit = async () => {
    if (!form.business_name || !form.phone) return
    setSubmitting(true)
    const { error } = await supabase.from('business_applications').insert([{
      business_name: form.business_name,
      contact_name: form.contact_name,
      phone: form.phone,
      city: form.city,
      status: 'new'
    }])
    setSubmitting(false)
    if (!error) setSubmitted(true)
  }

  const font = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '13px 14px',
    border: '1.5px solid rgba(0,0,0,0.08)', borderRadius: '12px',
    fontSize: '15px', fontFamily: font, outline: 'none', color: '#1C1C1E',
    background: 'rgba(255,255,255,0.7)'
  }

  const copy = {
    ru: {
      eyebrow: 'qaraa для бизнеса',
      title: 'Для бизнеса',
      subtitle: 'Подключите заведение к бонусной карте qaraa — и получите доступ к аудитории, которая уже пользуется картой в других местах города.',
      cta: 'Оставить заявку',
      ctaSecondary: 'Как это работает',
      statsLabel: ['партнёрских точек', 'держателей карты', 'средний рост повторных визитов'],
      statsValue: ['150+', '40 000+', '23%'],
      benefitsTitle: 'Что вы получаете',
      benefits: [
        { title: 'Повторные визиты', text: 'Кэшбэк мотивирует клиента вернуться именно к вам, а не к конкуренту через дорогу.' },
        { title: 'Новая аудитория', text: 'Держатели карты qaraa видят вашу точку в приложении и приходят целенаправленно.' },
        { title: 'Прозрачная аналитика', text: 'Видите количество визитов, средний чек и начисленный кэшбэк в личном кабинете.' },
        { title: 'Гибкие настройки', text: 'Сами задаёте процент кэшбэка и акции — без посредников и долгих согласований.' },
      ],
      howTitle: 'Как подключиться',
      steps: [
        { title: 'Оставляете заявку', text: 'Заполняете форму ниже — мы свяжемся в течение рабочего дня.' },
        { title: 'Настраиваем точку', text: 'Подключаем QR или терминал, объясняем персоналу, как это работает.' },
        { title: 'Начинаете получать клиентов', text: 'Заведение появляется в приложении, клиенты копят и тратят бонусы у вас.' },
      ],
      formTitle: 'Оставить заявку',
      formSubtitle: 'Расскажите немного о заведении — мы свяжемся, чтобы обсудить детали.',
      nameLabel: 'Название заведения',
      namePlace: 'Например, кофейня «Утро»',
      contactLabel: 'Контактное лицо',
      contactPlace: 'Имя Фамилия',
      phoneLabel: 'Телефон',
      cityLabel: 'Город',
      cityPlace: 'Астана',
      sendBtn: 'Отправить заявку',
      sending: 'Отправка...',
      successTitle: 'Заявка отправлена!',
      successSub: 'Мы свяжемся с вами в ближайшее время.',
    },
    en: {
      eyebrow: 'qaraa for business',
      title: 'For business',
      subtitle: 'Connect your venue to the qaraa loyalty card and reach an audience that already uses it around the city.',
      cta: 'Apply now',
      ctaSecondary: 'How it works',
      statsLabel: ['partner venues', 'card holders', 'avg. repeat visit growth'],
      statsValue: ['150+', '40 000+', '23%'],
      benefitsTitle: 'What you get',
      benefits: [
        { title: 'Repeat visits', text: 'Cashback gives customers a reason to come back to you instead of the place next door.' },
        { title: 'New audience', text: 'Cardholders see your venue in the app and come specifically to spend their bonuses.' },
        { title: 'Clear analytics', text: 'Track visits, average check, and accrued cashback from your dashboard.' },
        { title: 'Flexible setup', text: 'Set your own cashback rate and promotions, no middlemen involved.' },
      ],
      howTitle: 'How to join',
      steps: [
        { title: 'Submit an application', text: "Fill out the form below, we'll reach out within one business day." },
        { title: 'We set up your venue', text: 'We connect a QR code or terminal and walk your staff through it.' },
        { title: 'Start getting customers', text: 'Your venue appears in the app, customers earn and spend bonuses with you.' },
      ],
      formTitle: 'Apply now',
      formSubtitle: "Tell us a bit about your venue and we'll get in touch.",
      nameLabel: 'Venue name',
      namePlace: 'e.g. "Morning" coffee shop',
      contactLabel: 'Contact person',
      contactPlace: 'First and last name',
      phoneLabel: 'Phone',
      cityLabel: 'City',
      cityPlace: 'Astana',
      sendBtn: 'Send application',
      sending: 'Sending...',
      successTitle: 'Application sent!',
      successSub: 'We will contact you shortly.',
    },
    kk: {
      eyebrow: 'бизнеске арналған qaraa',
      title: 'Бизнеске',
      subtitle: 'Мекемеңізді qaraa бонус картасына қосыңыз — қаланың басқа жерлерінде картаны қолданатын аудиторияға қол жеткізесіз.',
      cta: 'Өтінім қалдыру',
      ctaSecondary: 'Бұл қалай жұмыс істейді',
      statsLabel: ['серіктес мекеме', 'карта иесі', 'қайталама келу өсімі'],
      statsValue: ['150+', '40 000+', '23%'],
      benefitsTitle: 'Сіз не аласыз',
      benefits: [
        { title: 'Қайталама келулер', text: 'Кэшбэк клиентті қайта сізге келуге ынталандырады.' },
        { title: 'Жаңа аудитория', text: 'Карта иелері қосымшада сіздің мекемеңізді көреді.' },
        { title: 'Ашық аналитика', text: 'Кабинетте келу саны, орташа чек және кэшбэкті көресіз.' },
        { title: 'Икемді баптау', text: 'Кэшбэк пайызы мен акцияларды өзіңіз белгілейсіз.' },
      ],
      howTitle: 'Қалай қосылу керек',
      steps: [
        { title: 'Өтінім қалдырасыз', text: 'Төмендегі форманы толтырыңыз — жұмыс күні ішінде хабарласамыз.' },
        { title: 'Мекемені баптаймыз', text: 'QR немесе терминал қосамыз, қызметкерлерге түсіндіреміз.' },
        { title: 'Клиенттер келе бастайды', text: 'Мекемеңіз қосымшада пайда болады, клиенттер бонус жинайды.' },
      ],
      formTitle: 'Өтінім қалдыру',
      formSubtitle: 'Мекеме туралы қысқаша айтып беріңіз — біз хабарласамыз.',
      nameLabel: 'Мекеме атауы',
      namePlace: 'Мысалы, «Таң» кафесі',
      contactLabel: 'Байланыс тұлғасы',
      contactPlace: 'Аты-жөні',
      phoneLabel: 'Телефон',
      cityLabel: 'Қала',
      cityPlace: 'Астана',
      sendBtn: 'Өтінімді жіберу',
      sending: 'Жіберілуде...',
      successTitle: 'Өтінім жіберілді!',
      successSub: 'Жақын арада хабарласамыз.',
    },
  }

  const c = copy[lang] || copy.ru

  return (
    <div style={{
      minHeight: '100vh',
      background: '#FAFAF8',
      fontFamily: font,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Декоративные сферы как в Login */}
      <div style={{
        position: 'fixed', width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(196, 184, 248, 0.6) 0%, transparent 70%)',
        top: -160, right: -100, pointerEvents: 'none', zIndex: 0
      }} />
      <div style={{
        position: 'fixed', width: 450, height: 450, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(248, 221, 184, 0.55) 0%, transparent 70%)',
        bottom: -180, left: -120, pointerEvents: 'none', zIndex: 0
      }} />

      {/* Fixed Header */}
      <header style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 100,
        background: 'transparent',
        transition: 'all 0.3s ease'
      }}>
        <div className="home-header-container" style={{
          maxWidth: '1440px', margin: '0 auto',
          padding: '20px 48px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div className="home-logo-container" style={{
            display: 'flex', alignItems: 'center', gap: '14px',
            cursor: 'pointer', transition: 'opacity 0.3s ease', opacity: 1
          }}
            onClick={() => navigate('/')}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <img src={logoQaraa} alt="qaraa" className="home-logo" style={{ height: '42px', width: 'auto', transition: 'transform 0.3s ease' }} />
            <div>
              <div className="home-logo-title" style={{ fontSize: '17px', fontWeight: '600', color: '#1C1C1E', lineHeight: '1.2' }}>
                <span className="logo-title-line1">qaraa.asia</span>
                <br className="mobile-break" />
                <span className="logo-title-line2 mobile-only-subtitle" style={{ fontSize: '12px', color: '#8E8E93', fontWeight: '500' }}>{t.logoSubtitle}</span>
              </div>
              <div className="home-logo-subtitle desktop-only-subtitle" style={{ fontSize: '12px', color: '#8E8E93', marginTop: '4px' }}>
                {t.logoSubtitle}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div ref={langRef} style={{ position: 'relative', marginRight: '6px' }}>
              <button onClick={() => setLangDropdownOpen(!langDropdownOpen)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: langDropdownOpen ? 'rgba(0,0,0,0.06)' : 'transparent', border: 'none', borderRadius: '100px', fontSize: '14px', fontWeight: '500', color: '#8E8E93', cursor: 'pointer', transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => { if (!langDropdownOpen) e.currentTarget.style.background = 'rgba(0,0,0,0.04)' }}
                onMouseLeave={(e) => { if (!langDropdownOpen) e.currentTarget.style.background = 'transparent' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                <span className="lang-text-label">{t.languageLabel}</span>
                <svg className="lang-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: langDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              {langDropdownOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#FFFFFF', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '6px', minWidth: '140px', zIndex: 200 }}>
                  {languages.map((l) => (
                    <button key={l.code} onClick={() => { changeLang(l.code); setLangDropdownOpen(false) }} style={{ display: 'block', width: '100%', padding: '10px 14px', background: lang === l.code ? 'rgba(0,122,255,0.08)' : 'transparent', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: lang === l.code ? '600' : '400', color: lang === l.code ? '#007AFF' : '#1C1C1E', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease' }}
                      onMouseEnter={(e) => { if (lang !== l.code) e.currentTarget.style.background = 'rgba(0,0,0,0.04)' }}
                      onMouseLeave={(e) => { if (lang !== l.code) e.currentTarget.style.background = 'transparent' }}>
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => navigate('/login')} className="home-header-button-login"
              style={{ padding: '10px 22px', background: 'transparent', border: 'none', borderRadius: '100px', fontSize: '15px', fontWeight: '600', color: '#000000', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', letterSpacing: '-0.3px' }}
              onMouseEnter={(e) => { e.target.style.background = 'rgba(0, 0, 0, 0.06)'; e.target.style.transform = 'scale(1.02)' }}
              onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.transform = 'scale(1)' }}>
              {t.login}
            </button>
            <button onClick={scrollToForm} className="home-header-button-register"
              style={{ padding: '10px 22px', background: '#FFFFFF', border: '1.5px solid #007AFF', borderRadius: '100px', fontSize: '15px', fontWeight: '600', color: '#007AFF', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', letterSpacing: '-0.3px' }}
              onMouseEnter={(e) => { e.target.style.background = '#007AFF'; e.target.style.color = '#FFFFFF'; e.target.style.transform = 'scale(1.02)' }}
              onMouseLeave={(e) => { e.target.style.background = '#FFFFFF'; e.target.style.color = '#007AFF'; e.target.style.transform = 'scale(1)' }}>
              {c.cta}
            </button>

            {/* Мобильное меню */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="home-mobile-menu-button"
              style={{ display: 'none', padding: '8px', background: 'transparent', border: 'none', cursor: 'pointer', flexDirection: 'column', gap: '5px' }}>
              <span style={{ width: '24px', height: '2px', background: '#000000', borderRadius: '2px', transition: 'all 0.3s', transform: mobileMenuOpen ? 'rotate(45deg) translateY(7px)' : 'none' }}></span>
              <span style={{ width: '24px', height: '2px', background: '#000000', borderRadius: '2px', transition: 'all 0.3s', opacity: mobileMenuOpen ? 0 : 1 }}></span>
              <span style={{ width: '24px', height: '2px', background: '#000000', borderRadius: '2px', transition: 'all 0.3s', transform: mobileMenuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none' }}></span>
            </button>
          </div>
        </div>
      </header>

      {/* Мобильное выпадающее меню */}
      {mobileMenuOpen && (
        <div className="home-mobile-menu" style={{
          position: 'fixed', top: '76px', left: 0, right: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          padding: '16px 20px', zIndex: 99,
          display: 'none', flexDirection: 'column', gap: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}>
          <button onClick={() => { navigate('/login'); setMobileMenuOpen(false) }}
            style={{ padding: '12px 20px', background: 'rgba(0, 0, 0, 0.06)', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', color: '#000000', cursor: 'pointer', width: '100%', letterSpacing: '-0.3px', transition: 'all 0.2s ease' }}>
            {t.login}
          </button>
          <button onClick={() => { scrollToForm(); setMobileMenuOpen(false) }}
            style={{ padding: '12px 20px', background: 'linear-gradient(180deg, #007AFF 0%, #0051D5 100%)', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', color: '#FFFFFF', cursor: 'pointer', width: '100%', letterSpacing: '-0.3px', boxShadow: '0 2px 8px rgba(0, 122, 255, 0.25)', transition: 'all 0.2s ease' }}>
            {c.cta}
          </button>
        </div>
      )}

      {/* Основной контент */}
      <main style={{ flex: 1, width: '100%', background: '#FFFFFF', paddingBottom: '40px' }}>
        <div className="business-wrapper" style={{ position: 'relative', zIndex: 1, padding: '150px 24px 80px', maxWidth: '900px', margin: '0 auto' }}>
          {/* Locked State */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            padding: '64px 24px',
            borderRadius: '32px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.05)',
            maxWidth: '600px', width: '100%', margin: '0 auto',
            textAlign: 'center'
          }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '24px', background: 'rgba(0, 122, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 32px' }}>
              <Lock size={40} color="#007AFF" />
            </div>
            <h2 style={{ fontSize: '28px', fontWeight: '700', letterSpacing: '-0.02em', color: '#1C1C1E', margin: '0 0 20px' }}>
              Временно закрыто
            </h2>
            <p style={{
              fontSize: '18px', lineHeight: '1.65',
              color: '#3a3a3c', margin: 0, fontWeight: '400'
            }}>
              Страница находится на стадии доработки. Наши юристы и маркетологи ещё спорят, как красиво всё описать, а программисты пока пьют кофе. Скоро здесь будет что-то полезное для вашего бизнеса.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        paddingTop: '80px', paddingRight: '24px',
        paddingLeft: '24px', paddingBottom: '50px',
        background: 'linear-gradient(135deg, #F2F2F7 0%, #E5E5EA 100%)',
        color: '#1C1C1E', position: 'relative',
        borderRadius: '60px 60px 0 0',
        zIndex: 2
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '50px', marginBottom: '50px'
          }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '800', marginBottom: '16px', color: '#1C1C1E', letterSpacing: '-0.5px' }}>qaraa.asia</div>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                <img src={qaraaCrmLogo} alt="qaraa × qaraa.crm" style={{ height: '100px', width: 'auto' }} />
              </div>
              <div style={{ fontSize: '15px', color: '#636366', fontWeight: '500' }}>{t.logoSubtitle}</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#8E8E93', marginBottom: '20px', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{t.footerNavigation}</div>
              {[
                { name: t.footerAbout, path: '/about-us' },
                { name: t.footerBonusCard, path: '/card-info' },
                { name: t.footerVacancies, path: '/vacancies' },
                { name: t.footerBusiness, path: '/business' }
              ].map(l => (
                <div key={l.path} onClick={() => navigate(l.path)} style={{ fontSize: '15px', marginBottom: '12px', cursor: 'pointer', color: '#1C1C1E', fontWeight: '500', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6'; e.currentTarget.style.paddingLeft = '8px' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1C1E'; e.currentTarget.style.paddingLeft = '0' }}>
                  {l.name}
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#8E8E93', marginBottom: '20px', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{t.footerSocial}</div>
              {[
                { name: 'Instagram', url: 'https://www.instagram.com/qaraa.kz?igsh=cWw3cmlsNmJ0ZHRi' },
                { name: 'TikTok', url: 'https://www.tiktok.com/@qaraa.kz?_r=1&_t=ZM-91EaJ8tj6Do' },
                { name: 'Telegram', url: 'https://t.me/qaraa_kz' },
                { name: 'WhatsApp', url: 'https://wa.me/77778307588' }
              ].map(s => (
                <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '15px', color: '#1C1C1E', fontWeight: '500', textDecoration: 'none', marginBottom: '12px', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6'; e.currentTarget.style.paddingLeft = '8px' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1C1E'; e.currentTarget.style.paddingLeft = '0' }}>
                  {s.name}
                </a>
              ))}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#8E8E93', marginBottom: '20px', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{t.footerInfo}</div>
              <div style={{ fontSize: '15px', marginBottom: '12px', color: '#1C1C1E', fontWeight: '500' }}>+7 777 830 75 88</div>
              <div style={{ fontSize: '15px', color: '#636366', fontWeight: '500' }}>{t.footerCity}</div>
              <div style={{ fontSize: '15px', marginTop: '12px', color: '#636366', fontWeight: '500' }}>{t.footerAddress}</div>
            </div>
          </div>
          <div style={{
            marginTop: '80px', paddingTop: '40px',
            borderTop: '2px solid rgba(0, 0, 0, 0.06)',
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexWrap: 'wrap', gap: '20px',
            fontSize: '15px', color: '#8E8E93', fontWeight: '600'
          }}>
            <div>{t.footerRights}</div>
            <div style={{ display: 'flex', gap: '24px' }}>
              <span onClick={() => navigate('/privacy')} style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#8E8E93' }}>
                {t.footerPrivacy}
              </span>
              <span onClick={() => navigate('/terms')} style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#8E8E93' }}>
                {t.footerTerms}
              </span>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 768px) {
          .lang-text-label { display: none !important; }
          .lang-chevron { display: none !important; }
          .home-logo-subtitle.desktop-only-subtitle { display: none !important; }
          .mobile-only-subtitle { display: none !important; }
          .home-header-container { padding: 14px 16px !important; }
          .home-logo-container { gap: 10px !important; }
          .home-header-button-login,
          .home-header-button-register { display: none !important; }
          .home-mobile-menu-button {
            display: flex !important;
            flex-direction: column !important;
            gap: 5px !important;
          }
          .home-mobile-menu { display: flex !important; }

          .business-wrapper { padding: 110px 16px 24px !important; }
          .business-title { font-size: 38px !important; letter-spacing: -1px !important; }
          .business-stats { padding: 24px !important; gap: 20px !important; }
          .business-benefits-grid { grid-template-columns: 1fr !important; gap: 14px !important; }
          .business-steps { flex-direction: column !important; gap: 22px !important; }
          .business-form-card { padding: 26px !important; }
          .business-form-row { flex-direction: column !important; }
        }
      `}</style>
    </div>
  )
}